import { AfterViewChecked, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AppStateStore } from '../../store/app-state.store';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatCard, MatCardContent, MatCardImage } from '@angular/material/card';
import { ResponsiveService } from '../../services/responsive.service';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { Story } from '../../model/story/story.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-story-list',
  imports: [
    AsyncPipe,
    MatCard,
    MatCardContent,
    MatCardImage,
    NgClass,
    RouterLink,
    MatIcon,
    MatChip,
    MatChipSet,
  ],
  templateUrl: './story-list.html',
  styleUrl: './story-list.scss',
})
export class StoryList implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('sentinel', {read: ElementRef}) sentinel?: ElementRef;

  public readonly appStateStore = inject(AppStateStore);
  public responsiveService = inject(ResponsiveService);

  // Lazy loading properties
  private allStories: Story[] = [];
  public displayedStories: Story[] = [];
  private readonly BATCH_SIZE = 25;
  private currentIndex = 0;
  private observer?: IntersectionObserver;
  private storyListSubscription?: Subscription;
  private isObserving = false;

  ngOnInit(): void {
    // Subscribe to the full story list
    this.storyListSubscription = this.appStateStore.storyList$.subscribe((stories: Story[]) => {
      this.allStories = stories;
      // Reset and load initial batch
      this.currentIndex = 0;
      this.displayedStories = [];
      this.isObserving = false;
      this.loadMoreStories();
    });

    // Set up Intersection Observer once
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMoreStories) {
            this.loadMoreStories();
          }
        });
      },
      {
        root: null,
        rootMargin: '100px', // Start loading 100px before reaching the sentinel
        threshold: 0.1,
      },
    );
  }

  ngAfterViewChecked(): void {
    // Observe the sentinel element when it becomes available
    if (this.sentinel?.nativeElement && !this.isObserving && this.hasMoreStories) {
      this.observer?.observe(this.sentinel.nativeElement);
      this.isObserving = true;
    } else if (!this.hasMoreStories && this.isObserving) {
      // Stop observing when there are no more stories
      this.observer?.disconnect();
      this.isObserving = false;
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.storyListSubscription?.unsubscribe();
  }

  private loadMoreStories(): void {
    const nextBatch = this.allStories.slice(
      this.currentIndex,
      this.currentIndex + this.BATCH_SIZE,
    );

    if (nextBatch.length > 0) {
      this.displayedStories = [...this.displayedStories, ...nextBatch];
      this.currentIndex += nextBatch.length;
    }
  }

  public get hasMoreStories(): boolean {
    return this.currentIndex < this.allStories.length;
  }
}
